import {
  EventCalendar,
  EventReservedTickets,
} from 'app/pages/TicketSalePage/slice/types';
import filter from 'lodash/filter';
import findIndex from 'lodash/findIndex';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { SelectedItems, SelectedItemType } from 'types/SelectedItem';
import { getSelectedTicketQuantityFromAllBundles } from 'utils/bundles';
import { dateFormatForCalendar } from 'utils/calendar';
import useEmbedResize from 'utils/custom-hooks/useEmbedResize';
import { Ticket, TicketTypeBundle } from '../../../types/TicketsState';
import {
  makeSelectedEvent,
  selectSelectedItems,
  selectVoucherData,
} from '../../pages/TicketSalePage/slice/selectors';
import BundledAddonsSection from '../BundledAddonsSection/BundledAddonsSection';
import { useCalendarController } from '../TicketSaleCard/Calendar/calendarContext';
import ChooseSeatsButton from './ChooseSeatsButton';
import { getBooleansForDisplayingTicketLabels } from './getBooleansForDisplayingTicketLabels';
import TicketBulkOptions from './TicketBulkOptions';
import TicketGenerics from './TicketRow/TicketGenerics';
import TicketPrice from './TicketRow/TicketPrice';
import TicketQuantityButtons from './TicketRow/TicketQuantityButtons';
import {
  getTicketAvailabilityDefault,
  getTicketAvailabilityOnCalendar,
  getTicketLimitOnCalendar,
} from './utils';
import { EVENT_TYPE_WITH_SEATING_CHART } from 'utils/static/constants';
import TicketSubtypesPriceRange from './TicketRow/TicketSubtypesPriceRange';

interface TicketSectionProps {
  ticket: Ticket;
  handleOpenChart?: () => void;
}

const TicketSection: React.FC<TicketSectionProps> = ({
  ticket,
  handleOpenChart,
}) => {
  const selectedItems = useSelector(selectSelectedItems);
  const {
    type,
    ticketTypeBundles,
    dateTimeFormat: { timezone },
    timeInterval: { endsAt },
    calendar,
    reservedTickets,
  } = useSelector(makeSelectedEvent);
  const {
    id,
    description,
    ticketaddons,
    sold_to,
    sold_from,
    hasCalendar,
    seatsColor,
    seatsCategoryKey,
    subtypes,
  } = ticket;

  // to be refactored ( quick and dirty solution )
  let selectedDate: DateTime | undefined;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { selectedDate: selectedDateFromContext } = useCalendarController();
    selectedDate = selectedDateFromContext;
  } catch (e) {}

  // --- Bundled Addons ---
  const bundledAddons = filter(ticketaddons, {
    is_bundled: 1,
  });

  // --- Voucher ---
  const { voucherApplicableTickets, status: voucherStatus } =
    useSelector(selectVoucherData);

  const ticketHasDiscount =
    voucherStatus === 'valid' &&
    !!voucherApplicableTickets.find(ticket => ticket === id);

  // --- Ticket Quantity ---
  const index =
    hasCalendar && selectedDate
      ? findIndex(selectedItems, {
          type: SelectedItemType.TICKET,
          ticketId: id,
          date: selectedDate.toFormat(dateFormatForCalendar),
        })
      : findIndex(selectedItems, {
          type: SelectedItemType.TICKET,
          ticketId: id,
        });
  const initialQuantity = index !== -1 ? selectedItems[index]?.quantity : 0;
  const [quantity, setQuantity] = useState(initialQuantity);

  if (
    selectedItems[index]?.quantity &&
    selectedItems[index]?.quantity !== quantity
  ) {
    setQuantity(selectedItems[index].quantity);
  }

  // useEmbedResize([quantity]);

  const ticketAvailableQuantity = getTicketAvailableQuantity(
    ticketTypeBundles,
    ticket,
    calendar,
    selectedDate,
    reservedTickets,
    selectedItems,
  );

  // --- Constants ---
  const { displaySoldToDate, displaySoldFromDate, ticketIsSellable } = useMemo(
    () =>
      getBooleansForDisplayingTicketLabels(
        endsAt,
        timezone,
        sold_from,
        sold_to,
        ticketAvailableQuantity === 0,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ticketAvailableQuantity],
  );

  return (
    <div className="border-b border-neutral-40 px-[12px] relative">
      {seatsColor ? (
        <div
          className="absolute left-0 h-full w-[6px]"
          style={{ backgroundColor: seatsColor }}
        />
      ) : null}
      <div
        className={`relative flex items-start sm:items-center justify-between sm:px-[12px] pb-[32px] pt-[12px] ${
          type === EVENT_TYPE_WITH_SEATING_CHART
            ? 'md:pb-[12px]'
            : 'sm:pb-[12px]'
        }`}
      >
        <TicketGenerics
          ticket={ticket}
          displaySoldToDate={displaySoldToDate}
          displaySoldFromDate={displaySoldFromDate}
          ticketHasDiscount={ticketHasDiscount}
          selectedDate={selectedDate}
          ticketAvailableQuantity={ticketAvailableQuantity}
        />
        {subtypes && subtypes.length ? (
          <TicketSubtypesPriceRange subtypes={subtypes} />
        ) : (
          <TicketPrice ticket={ticket} ticketHasDiscount={ticketHasDiscount} />
        )}
        {handleOpenChart ? (
          <ChooseSeatsButton
            handleOpenChart={handleOpenChart}
            seatsCategoryKey={seatsCategoryKey}
          />
        ) : (
          <TicketQuantityButtons
            ticketId={id}
            quantity={quantity}
            setQuantity={setQuantity}
            ticketHasCalendar={hasCalendar}
            ticketAvailableQuantity={ticketAvailableQuantity}
            ticketIsSellable={ticketIsSellable}
            selectedDate={selectedDate}
          />
        )}
      </div>

      {ticket.ticketprice.length > 1 ? (
        // to do: lazy load this component after we start using Observer API
        <TicketBulkOptions
          ticketId={id}
          ticketPrice={ticket.ticketprice}
          quantity={quantity}
          ticketAvailableQuantity={ticketAvailableQuantity}
          selectedDate={selectedDate}
        />
      ) : null}

      {description ? (
        <div className="sm:px-[12px] pb-[12px] whitespace-pre-line p3-regular text-neutral-800">
          {description}
        </div>
      ) : null}
      {bundledAddons.length > 0 ? (
        <BundledAddonsSection addons={bundledAddons} />
      ) : null}
    </div>
  );
};

export default TicketSection;

const getBundlesForTicket = (
  ticketTypeBundles: TicketTypeBundle[] | undefined,
  id: string,
) =>
  ticketTypeBundles?.filter(ticketTypeBundle =>
    ticketTypeBundle.items.some(item => item.idTicketType === id),
  );

const getTicketAvailableQuantity = (
  ticketTypeBundles: TicketTypeBundle[] | undefined,
  ticket: Ticket,
  calendar: EventCalendar | undefined,
  selectedDate: DateTime | undefined,
  reservedTickets: EventReservedTickets | undefined,
  selectedItems: SelectedItems,
) => {
  const bundlesWhichIncludeThisTicket = getBundlesForTicket(
    ticketTypeBundles,
    ticket.id,
  );

  if (!bundlesWhichIncludeThisTicket) {
    return ticket.hasCalendar && selectedDate && calendar
      ? getTicketAvailabilityOnCalendar(
          calendar,
          reservedTickets!,
          ticket.id,
          selectedDate,
        )
      : getTicketAvailabilityDefault(ticket.limit, ticket.maxOrderQuantity);
  }

  const limit =
    ticket.hasCalendar && selectedDate && calendar
      ? getTicketLimitOnCalendar(
          calendar!,
          reservedTickets!,
          ticket.id,
          selectedDate!,
        )
      : ticket.limit;

  if (limit === null) return ticket.maxOrderQuantity;

  const availableQuantity =
    limit -
    getSelectedTicketQuantityFromAllBundles(
      bundlesWhichIncludeThisTicket,
      selectedItems,
      ticket.id,
      selectedDate,
    );

  return Math.min(availableQuantity, ticket.maxOrderQuantity);
};
